import React, { useState, type FormEvent } from "react";

export interface EmailConfirmationFormProps {
  onSubmit: (code: string) => void | Promise<void>;
  /** Omit to hide the "Resend code" action. */
  onResend?: () => void | Promise<void>;
  loading?: boolean;
  resending?: boolean;
  error?: string | null;
  /** Shown in the helper text ("We sent a code to {email}") when provided. */
  email?: string;
  className?: string;
  codeLabel?: string;
}

/** Headless-friendly confirmation-code form. Bring your own container via `className`; every element carries a stable `fka-*` class for restyling. */
export function EmailConfirmationForm({
  onSubmit,
  onResend,
  loading = false,
  resending = false,
  error,
  email,
  className,
  codeLabel = "Confirmation code",
}: EmailConfirmationFormProps) {
  const [code, setCode] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      setFieldError("Enter the code we sent you.");
      return;
    }
    setFieldError(null);
    await onSubmit(trimmed);
  };

  return (
    <form className={className ?? "fka-confirm-email-form flex flex-col gap-4"} onSubmit={handleSubmit} noValidate>
      {email && (
        <p className="fka-confirm-email-hint text-sm text-slate-400">
          We sent a confirmation code to <span className="font-medium text-slate-300">{email}</span>.
        </p>
      )}

      <div className="fka-field flex flex-col gap-1">
        <label htmlFor="fka-confirm-code" className="fka-label text-sm font-medium text-slate-300">
          {codeLabel}
        </label>
        <input
          id="fka-confirm-code"
          name="code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          aria-invalid={Boolean(fieldError)}
          aria-describedby={fieldError ? "fka-confirm-code-error" : undefined}
          className="fka-input rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100 outline-none focus:border-amber-500"
        />
        {fieldError && (
          <p id="fka-confirm-code-error" className="fka-field-error text-sm text-red-400">
            {fieldError}
          </p>
        )}
      </div>

      {error && (
        <p role="alert" className="fka-server-error text-sm text-red-400">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="fka-submit rounded-md bg-amber-500 px-4 py-2 font-medium text-slate-950 transition disabled:cursor-not-allowed disabled:opacity-60 hover:bg-amber-400"
      >
        {loading ? "Confirming…" : "Confirm email"}
      </button>

      {onResend && (
        <button
          type="button"
          onClick={() => onResend()}
          disabled={resending}
          className="fka-resend text-sm font-medium text-slate-400 underline-offset-2 hover:text-amber-400 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
        >
          {resending ? "Sending…" : "Resend code"}
        </button>
      )}
    </form>
  );
}
